import { ReactNode, useId } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import { useTranslation } from 'react-i18next';

interface PageFiltersProps {
  children: ReactNode;
  actions?: ReactNode;
  alignItems?: 'center' | 'stretch';
}

const PageFilters = ({
  children,
  actions,
  alignItems = 'center',
}: PageFiltersProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const id = useId();

  if (!isCompact) {
    return (
      <Paper
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'row',
          flexWrap: 'wrap',
          mt: 1.5,
          gap: 1.5,
          p: 2.5,
          alignItems,
        }}
      >
        {children}
        {actions && <Box sx={{ flexGrow: 1 }} />}
        {actions}
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 1.5, minWidth: 0 }}>
      <Accordion disableGutters>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          id={`${id}-summary`}
          aria-controls={`${id}-filters`}
          sx={{ minHeight: 56 }}
        >
          <TuneIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography sx={{ fontWeight: 500 }}>
            {t('shared.filters')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            p: 2,
            pt: 1,
            '& > *': {
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              flex: '0 0 auto',
              m: 0,
            },
            '& .MuiFormControl-root, & .MuiAutocomplete-root': {
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              flex: '0 0 auto',
            },
          }}
        >
          {children}
        </AccordionDetails>
      </Accordion>
      {actions && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1.5,
            mt: 1,
            '& > *': { minWidth: 0, maxWidth: '100%' },
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default PageFilters;
